import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  GrowProjectsResponse,
  InyovaGrowOnboarding,
  InyovaGrowPercentages,
  InyovaGrowRisk,
  InyovaGrowSubmit,
  InyovaGrowSubmitResponse,
  InyovaGrowSurveyQuestion
} from '@inyova/grow';
import { ApiAccount, Customer, GrowAccount } from '@inyova/models';
import { Jsona } from 'jsona';
import type { TJsonApiBody } from 'jsona/lib/JsonaTypes';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class GrowService {
  private readonly URL_V2: string;
  private readonly URL_V3: string;
  private readonly URL_V4: string;
  private jsona = new Jsona();

  constructor(private http: HttpClient) {
    this.URL_V2 = `${environment.apiUrl}`;
    this.URL_V3 = `${environment.apiUrlV3}`;
    this.URL_V4 = `${environment.apiUrlV4}`;
  }

  getTargetUrlBasedOnStepStatus(currentAccountDetail: GrowAccount, customer: Customer): string {
    let targetUrl: string | null = null;
    const { step_status, customer_journey } = currentAccountDetail;
    const hasBankApplicationInProgress = customer.temporary_accounts.some((account) => account.kind === '3b' && account.step_status === 3);

    if (hasBankApplicationInProgress && 1 <= step_status && step_status <= 3) {
      return '/complete-bank-setup';
    }

    switch (true) {
      case step_status === 1:
        targetUrl = '/grow/risk';
        break;
      case step_status === 2:
        targetUrl = '/next/saxo-account';
        break;
      case step_status === 3:
        if (customer_journey?.id_verification_completed) {
          targetUrl = '/next/fund';
        } else {
          targetUrl = '/next/saxo-account';
        }

        break;
      case step_status >= 4:
      default:
        targetUrl = '/grow';
        break;
    }
    return targetUrl;
  }

  getGrowAccount(accountID: string): Observable<ApiAccount> {
    return this.http.get<ApiAccount>(`${this.URL_V2}/accounts/${accountID}`);
  }

  createGrowAccount(): Observable<ApiAccount> {
    return this.http.post<ApiAccount>(`${this.URL_V2}/accounts`, { account: { kind: 'grow' } });
  }

  getGrowRisk(): Observable<{ data: InyovaGrowRisk; percentages: InyovaGrowPercentages }> {
    return this.http
      .get<TJsonApiBody>(`${this.URL_V3}/inyova_interest/interest_onboarding`, {
        params: {
          include: ['risk_survey', 'financial_detail', 'recommended_projects_percentage'].toString()
        }
      })
      .pipe(
        map((response) => {
          const { recommended_projects_percentage: percentages, ...data } = this.jsona.deserialize({ ...response }) as InyovaGrowOnboarding;
          return { data, percentages };
        })
      );
  }

  checkGrowRiskProfile(
    initial_investment: number,
    freely_cash_available: number,
    survey_questions: InyovaGrowSurveyQuestion[]
  ): Observable<InyovaGrowPercentages> {
    return this.http
      .post<TJsonApiBody>(
        `${this.URL_V3}/inyova_interest/recommended_projects_percentages`,
        this.jsona.serialize({
          stuff: {
            initial_investment,
            freely_cash_available,
            survey_questions,
            type: 'recommended_projects_percentage'
          }
        })
      )
      .pipe(
        map((response) => {
          return this.jsona.deserialize({ ...response }) as InyovaGrowPercentages;
        })
      );
  }

  updateGrowRisk(data: InyovaGrowSubmit, id: string, growOnboardingId: string): Observable<InyovaGrowSubmitResponse> {
    const body = this.jsona.serialize({
      stuff: {
        type: 'interest_onboarding',
        id: growOnboardingId,
        interest_account: { ...data.interestAccount, investment_risk_consent: true, id },
        risk_survey: data.riskSurvey,
        financial_detail: data.financialDetail,
        relationshipNames: ['interest_account', 'risk_survey', 'financial_detail']
      },
      includeNames: ['interest_account', 'risk_survey', 'financial_detail']
    });

    // Add temp-id to relationships
    Object.keys((body.data as any).relationships).forEach((item) => {
      const dataItem = (body.data as any).relationships[item].data;
      dataItem.method = 'update';
      if (item === 'risk_survey') {
        dataItem['temp-id'] = '1';
      }
    });

    // Add temp-id to risk_survey resource
    const res = body.included.find((item) => item.type === 'risk_survey');
    res['id'] = '1';

    return this.http
      .put<TJsonApiBody>(`${this.URL_V3}/inyova_interest/interest_onboarding`, body)
      .pipe(map((response) => this.jsona.deserialize({ ...response }) as InyovaGrowSubmitResponse));
  }

  toggleGrowProjectSelection(projectId: string, isSelected: boolean): Observable<void> {
    return this.http.post<void>(`${this.URL_V4}/inyova_interest/impact_projects/${projectId}/${isSelected ? 'mark' : 'unmark'}`, {});
  }

  getGrowProjects(): Observable<GrowProjectsResponse> {
    return this.http.get<GrowProjectsResponse>(`${this.URL_V4}/inyova_interest/universe`);
  }
}
